import {
  Timestamp,
  arrayUnion,
  collection,
  doc,
  getDocs,
  onSnapshot,
  query,
  runTransaction,
  setDoc,
  updateDoc,
  where,
} from 'firebase/firestore';
import { auth, db } from '@/firebase';
import { buildIncidentRecord, cleanIncidentText as cleanText, normalizeIncidentStaff, normalizeIncidentParticipants } from '@/services/domain/incidentRecording';
export { INCIDENT_PARTICIPANT_ROLES, normalizeIncidentStaff, normalizeIncidentParticipants } from '@/services/domain/incidentRecording';
import { createFirestoreDocumentOnce } from '@/services/domain/createFirestoreRecordOnce';

export const INCIDENT_STATUS = Object.freeze({
  OPEN: 'open',
  CLOSED: 'closed',
});



export const INCIDENT_ACTION_TYPES = Object.freeze([
  'consequence',
  'counselling',
  'restorative-conversation',
  'parent-meeting',
  'monitoring',
  'apology',
  'repair-task',
  'referral',
  'other',
]);

export const INCIDENT_FOLLOW_UP_STATES = Object.freeze([
  'not-started',
  'in-progress',
  'waiting',
  'complete',
]);

const incidentsPath = (schoolId) => `schools/${schoolId}/incidents`;

export function incidentAuditEntries(incident = {}) {
  return [...(incident.audit || []), ...(incident.history || [])].sort((a, b) => {
    const aTime = a.at?.toMillis?.() || new Date(a.at || 0).getTime();
    const bTime = b.at?.toMillis?.() || new Date(b.at || 0).getTime();
    return bTime - aTime;
  });
}

export function incidentReferenceKey(reference = {}) {
  return [
    reference.kind || 'record',
    reference.collection || reference.sourceType || '',
    reference.recordId || reference.sourceId || '',
    reference.attachmentId || '',
  ].join(':');
}

export function upsertIncidentReference(references = [], reference) {
  const key = incidentReferenceKey(reference);
  return [
    ...references.filter((item) => incidentReferenceKey(item) !== key),
    { ...reference, key },
  ];
}

export function removeIncidentReference(references = [], reference) {
  const key = typeof reference === 'string' ? reference : incidentReferenceKey(reference);
  return references.filter((item) => incidentReferenceKey(item) !== key && item.key !== key);
}

export function isIncidentReportReference(reference = {}) {
  return reference.recordType === 'incident_report'
    || reference.type === 'incident_report'
    || (
      (reference.collection === 'studentProfileItems' || reference.sourceType === 'studentProfileItem')
      && reference.kind === 'record'
    );
}

export function incidentHasStaffReport(incident = {}) {
  return (incident.recordRefs || []).some(isIncidentReportReference);
}

function auditEntry(action, actor = {}, detail = {}) {
  return {
    action,
    actorId: actor.uid || actor.userId || null,
    actorName: actor.displayName || actor.userName || actor.email || 'Staff member',
    at: Timestamp.now(),
    ...detail,
  };
}

export async function createIncident(input) {
  const { schoolId, actor, issuanceId } = input;
  const { id, data } = buildIncidentRecord(input, Timestamp.now());
  const incidentRef = id ? doc(db, incidentsPath(schoolId), id) : doc(collection(db, incidentsPath(schoolId)));
  if (issuanceId) {
    // Staff can file a sealed case but cannot read its contents. A create-only
    // acknowledgement reuses the actor's opaque submission ID without exposing
    // case data or granting the read permission needed by a transaction.
    await createFirestoreDocumentOnce({
      projectId: db.app.options.projectId, collectionPath: incidentsPath(schoolId),
      id: incidentRef.id, data,
      getToken: () => {
        if (auth.currentUser?.uid !== actor.uid) throw new Error('Sign in as the recorder before retrying.');
        return auth.currentUser.getIdToken();
      },
      ...(import.meta.env.DEV && import.meta.env.VITE_USE_EMULATORS === 'true' ? {
        origin: `http://${import.meta.env.VITE_FIREBASE_EMULATOR_HOST || 'localhost'}:${import.meta.env.VITE_FIRESTORE_EMULATOR_PORT || 8080}`,
      } : {}),
    });
  } else {
    await setDoc(incidentRef, data);
  }

  return incidentRef.id;
}

async function updateReferences(schoolId, incidentId, field, reference, actor, remove = false) {
  if (!schoolId || !incidentId) throw new Error('Incident link is missing its school or incident.');
  const target = doc(db, incidentsPath(schoolId), incidentId);
  const key = incidentReferenceKey(reference);

  if (!remove) {
    const linkedReference = { ...reference, key };
    const updates = {
      [field]: arrayUnion(linkedReference),
      updatedAt: Timestamp.now(),
      audit: arrayUnion(auditEntry('reference-linked', actor, {
        referenceKey: key,
        referenceKind: reference.kind || (field === 'fileRefs' ? 'file' : 'record'),
        label: reference.label || reference.fileName || '',
      })),
    };
    if (field === 'recordRefs' && isIncidentReportReference(reference)) {
      updates['documentation.staffReport'] = 'complete';
    }
    await updateDoc(target, updates);
    return;
  }

  await runTransaction(db, async (transaction) => {
    const snapshot = await transaction.get(target);
    if (!snapshot.exists()) throw new Error('Incident not found.');
    const current = snapshot.data()[field] || [];
    const next = removeIncidentReference(current, key);
    const updates = {
      [field]: next,
      updatedAt: Timestamp.now(),
      audit: arrayUnion(auditEntry('reference-unlinked', actor, {
        referenceKey: key,
        referenceKind: reference.kind || (field === 'fileRefs' ? 'file' : 'record'),
        label: reference.label || reference.fileName || '',
      })),
    };
    transaction.update(target, updates);
  });
}

export function linkRecordToIncident(schoolId, incidentId, reference, actor) {
  return updateReferences(schoolId, incidentId, 'recordRefs', {
    kind: 'record',
    ...reference,
  }, actor);
}

export function linkFileToIncident(schoolId, incidentId, reference, actor) {
  return updateReferences(schoolId, incidentId, 'fileRefs', {
    kind: 'file',
    ...reference,
  }, actor);
}

export function unlinkFileFromIncident(schoolId, incidentId, reference, actor) {
  return updateReferences(schoolId, incidentId, 'fileRefs', {
    kind: 'file',
    ...reference,
  }, actor, true);
}

export async function updateIncidentPeople(schoolId, incidentId, { participants, staff, assignedStaff, reason = '' } = {}, actor) {
  const updates = {
    updatedAt: Timestamp.now(),
    audit: arrayUnion(auditEntry('incident-people-updated', actor, { reason: cleanText(reason) })),
  };
  if (participants) updates.participants = normalizeIncidentParticipants(participants);
  if (staff) updates.staff = normalizeIncidentStaff(staff);
  if (assignedStaff) updates.assignedStaff = normalizeIncidentStaff(assignedStaff);
  await updateDoc(doc(db, incidentsPath(schoolId), incidentId), updates);
}

export async function updateIncidentDetails(schoolId, incidentId, details = {}, actor) {
  const updates = {
    updatedAt: Timestamp.now(),
    audit: arrayUnion(auditEntry('incident-details-updated', actor)),
  };
  ['summary', 'narrative', 'immediateAction', 'incidentLocation', 'reportType', 'severity'].forEach((field) => {
    if (Object.prototype.hasOwnProperty.call(details, field)) updates[field] = cleanText(details[field]);
  });
  if (details.occurredAt) updates.occurredAt = details.occurredAt;
  await updateDoc(doc(db, incidentsPath(schoolId), incidentId), updates);
}

export async function addIncidentStatement(schoolId, incidentId, statement = {}, actor) {
  const text = cleanText(statement.text);
  const speakerName = cleanText(statement.speakerName);
  if (!text || !speakerName) throw new Error('A speaker and statement are required.');
  const entry = {
    id: `${Date.now()}-${actor?.uid || actor?.userId || 'staff'}`,
    speakerId: statement.speakerId || null,
    speakerName,
    speakerType: statement.speakerType || 'other',
    text,
    capturedAt: Timestamp.now(),
    capturedBy: actor?.uid || actor?.userId || null,
    capturedByName: actor?.displayName || actor?.userName || actor?.email || 'Staff member',
  };
  await updateDoc(doc(db, incidentsPath(schoolId), incidentId), {
    statements: arrayUnion(entry),
    updatedAt: Timestamp.now(),
    audit: arrayUnion(auditEntry('incident-statement-added', actor, { speakerName })),
  });
  return entry;
}

export async function addIncidentNote(schoolId, incidentId, note, actor) {
  const text = cleanText(note);
  if (!text) throw new Error('A note is required.');
  const entry = {
    id: `${Date.now()}-${actor?.uid || actor?.userId || 'staff'}`,
    text,
    internal: true,
    createdAt: Timestamp.now(),
    createdBy: actor?.uid || actor?.userId || null,
    createdByName: actor?.displayName || actor?.userName || actor?.email || 'Staff member',
  };
  await updateDoc(doc(db, incidentsPath(schoolId), incidentId), {
    notes: arrayUnion(entry),
    updatedAt: Timestamp.now(),
    audit: arrayUnion(auditEntry('incident-note-added', actor)),
  });
  return entry;
}

export async function updateIncidentFollowUp(schoolId, incidentId, followUp = {}, actor) {
  const state = INCIDENT_FOLLOW_UP_STATES.includes(followUp.state) ? followUp.state : 'not-started';
  await updateDoc(doc(db, incidentsPath(schoolId), incidentId), {
    followUp: {
      owner: followUp.owner || null,
      dueDate: followUp.dueDate || null,
      notes: cleanText(followUp.notes),
      state,
    },
    updatedAt: Timestamp.now(),
    audit: arrayUnion(auditEntry('incident-follow-up-updated', actor, { state })),
  });
}

export async function addIncidentAction(schoolId, incidentId, action = {}, actor) {
  const type = INCIDENT_ACTION_TYPES.includes(action.type) ? action.type : 'other';
  const createdAt = Timestamp.now();
  const baseAction = {
    id: `${Date.now()}-${actor?.uid || actor?.userId || 'staff'}`,
    type,
    label: cleanText(action.label, type),
    notes: cleanText(action.notes),
    studentId: action.studentId || null,
    studentName: action.studentName || null,
    createdAt,
    createdBy: actor?.uid || actor?.userId || null,
    createdByName: actor?.displayName || actor?.userName || actor?.email || 'Staff member',
  };

  if (type === 'consequence') {
    const { disciplineService } = await import('@/services/disciplineService');
    const consequence = await disciplineService.createDisciplineConsequence({
      ...action.consequenceData,
      schoolId,
      studentId: action.studentId,
      studentName: action.studentName,
      className: action.className || '',
      incidentId,
      notes: cleanText(action.notes),
      issuedById: actor?.uid || actor?.userId || null,
      issuedByName: actor?.displayName || actor?.userName || actor?.email || 'Staff member',
      createdBy: actor?.uid || actor?.userId || null,
      createdByName: actor?.displayName || actor?.userName || actor?.email || 'Staff member',
      autoGenerated: false,
    });
    baseAction.consequenceId = consequence?.id || null;
    baseAction.label = action.label || consequence?.typeName || consequence?.type || 'Consequence';
  }

  await updateDoc(doc(db, incidentsPath(schoolId), incidentId), {
    actions: arrayUnion(baseAction),
    updatedAt: Timestamp.now(),
    audit: arrayUnion(auditEntry('incident-action-added', actor, {
      actionType: type,
      consequenceId: baseAction.consequenceId || null,
    })),
  });
  return baseAction;
}

export async function setIncidentStatus(schoolId, incidentId, status, actor) {
  if (![INCIDENT_STATUS.OPEN, INCIDENT_STATUS.CLOSED].includes(status)) {
    throw new Error('Unsupported incident status.');
  }
  await updateDoc(doc(db, incidentsPath(schoolId), incidentId), {
    status,
    updatedAt: Timestamp.now(),
    closedAt: status === INCIDENT_STATUS.CLOSED ? Timestamp.now() : null,
    closedBy: status === INCIDENT_STATUS.CLOSED ? actor?.uid || null : null,
    audit: arrayUnion(auditEntry(status === INCIDENT_STATUS.CLOSED ? 'incident-closed' : 'incident-reopened', actor)),
  });
}

export function subscribeToIncidents(schoolId, callback, { openOnly = false } = {}, onError = console.error) {
  if (!schoolId) {
    callback([]);
    return () => {};
  }
  const base = collection(db, incidentsPath(schoolId));
  const incidentQuery = openOnly ? query(base, where('status', '==', INCIDENT_STATUS.OPEN)) : base;
  return onSnapshot(
    incidentQuery,
    (snapshot) => {
      const incidents = snapshot.docs.map((item) => ({ id: item.id, ...item.data() }));
      incidents.sort((a, b) => {
        const aTime = a.updatedAt?.toMillis?.() || a.createdAt?.toMillis?.() || 0;
        const bTime = b.updatedAt?.toMillis?.() || b.createdAt?.toMillis?.() || 0;
        return bTime - aTime;
      });
      callback(incidents);
    },
    onError
  );
}

export async function getIncidents(schoolId, { openOnly = false } = {}) {
  if (!schoolId) return [];
  const base = collection(db, incidentsPath(schoolId));
  const incidentQuery = openOnly ? query(base, where('status', '==', INCIDENT_STATUS.OPEN)) : base;
  const snapshot = await getDocs(incidentQuery);
  return snapshot.docs
    .map((item) => ({ id: item.id, ...item.data() }))
    .sort((a, b) => {
      const aTime = a.updatedAt?.toMillis?.() || a.createdAt?.toMillis?.() || 0;
      const bTime = b.updatedAt?.toMillis?.() || b.createdAt?.toMillis?.() || 0;
      return bTime - aTime;
    });
}

export default {
  createIncident,
  linkRecordToIncident,
  linkFileToIncident,
  unlinkFileFromIncident,
  updateIncidentPeople,
  updateIncidentDetails,
  addIncidentStatement,
  addIncidentNote,
  addIncidentAction,
  updateIncidentFollowUp,
  setIncidentStatus,
  subscribeToIncidents,
  getIncidents,
};
